import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useHistory } from 'react-router'
import { useSelector } from 'react-redux'
import './PlayScreen.css'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import MakeContext from '../Make/MakeContext'
import ContentEngine from '../ContentEngine/ContentEngine'
import { GrPrevious, GrNext } from 'react-icons/gr'
import renderImage from '../../source/importImg'
import renderPrgImage from '../../source/programImg'
import renderCompImg from '../../source/Comp_Img'
import SliderRange from '../ReusableComponents/Slider/Slider'
import RGBSlider from './RGBSlider'
import MotorSlider from './MotorSlider'
import LiveButtonControl from './LiveButtonControl'
import LiveTackButtonControl from './LiveTackButtonControl'
import LiveHoldButtonControl from './LiveHoldButtonControl'
import LiveTriggerButtonControl from './LiveTriggerButtonControl'
import '@google/model-viewer'
import getBackendImage from '../../source/imageUtil'
import { IoInformationCircle } from 'react-icons/io5'

import {
    MdOutlineArrowBack,
    MdOutlineViewInAr,
    MdFullscreen,
    MdClosedCaption,
} from 'react-icons/md'
import { BsStars } from 'react-icons/bs'
import { RxReset } from 'react-icons/rx'

const BACKEND_URL = process.env.REACT_APP_API_BASE

// --- Momentary-button serialization tuning ---
// The board acknowledges a command with "Rdone" the moment it STARTS the action
// (it also echoes e.g. "PLAY MODE: WIGGLE") and replies "busy" if a command
// arrives while an action is still running. There is NO explicit
// "action finished" signal, so after a press we keep the buttons locked for
// ACTION_HOLD_MS past the board's most recent response, and re-lock if a press
// that slips through comes back "busy" (self-correcting for longer actions).
const ACTION_HOLD_MS = 2500
// A "busy" reply is only treated as ours (→ re-lock) if a tack press happened
// within this window; otherwise it's an unrelated/boot-time busy and ignored.
const PRESS_RELEVANCE_MS = 6000

const Ports = (port) => [0, 0]

// Normalise a step's live field to dict format.
// Old schema stored live as an array: [{inputId, ...}, ...]
// New schema stores live as a dict:   { inputId: {...}, ... }
const normalizeLive = (live) => {
    if (!live) return {}
    const applyDefaults = (entry) => ({
        isFree: true,
        compsIds: {},
        extraTargets: [],
        ...entry,
    })
    if (Array.isArray(live)) {
        return live.reduce((dict, entry) => {
            const { inputId, ...rest } = entry
            if (inputId) dict[inputId] = applyDefaults(rest)
            return dict
        }, {})
    }
    return Object.fromEntries(
        Object.entries(live).map(([k, v]) => [k, applyDefaults(v)])
    )
}

// Shared numeric comparison helper used by evaluateCondition below
const applyNumericOperator = (currentValue, operator, targetValue) => {
    const target = Number(targetValue)
    switch (operator) {
        case '==':
            return currentValue === target
        case '>':
            return currentValue > target
        case '<':
            return currentValue < target
        case '>=':
            return currentValue >= target
        case '<=':
            return currentValue <= target
        case '!=':
            return currentValue !== target
        default:
            return false
    }
}

function PlayScreen({
    data: dataProp,
    projectId: propProjectId,
    screenId: propScreenId,
    initialStep,
}) {
    const history = useHistory()

    // --- NEW: Parse URL inside the component for dynamic preview routing ---
    const urlParams = new URLSearchParams(
        history.location?.search || window.location.search
    )
    const isPreviewMode = urlParams.get('preview') === 'true'
    const queryProjectId = urlParams.get('projectId')
    const screenId = propScreenId || urlParams.get('screenId') || null

    const makeCtx = useContext(MakeContext)
    const isMake = !!makeCtx
    const { setHeaderProps, setFooterProps, setToolbarProps } = makeCtx || {}
    const isShellMode = isMake && !!setHeaderProps

    const HeaderComponent = MakeHeader

    // Check if we're in confined play mode (from KitMenu Play button)
    const isConfinedPlay = sessionStorage.getItem('isConfinedPlay') === 'true'
    const subprogramId = sessionStorage.getItem('playSubprogramId')

    // When launched from the Kit Menu Play button, the final injected
    // "Congratulations" step is skipped. Only applies to standalone route play
    // (never to embedded dataProp flows like ContentEngine / shell mode).
    const hideFinalStep =
        !dataProp && sessionStorage.getItem('playHideFinalStep') === 'true'

    // Fallback order: Props -> URL Query (Admin) -> Session (Normal) -> Default
    const projectId =
        propProjectId ||
        (dataProp
            ? dataProp.projectId
            : queryProjectId || subprogramId || 'talking-dragon')

    const modelRefs = useRef({})
    const prevOutputs = useRef({})
    const audioRef = useRef(null)
    const appContainerRef = useRef(null)
    const safeAreaRef = useRef(null)
    const prevLiveStatesRef = useRef({})
    // Always-current snapshot of outputStates, used for seeding prevLiveStatesRef on step entry.
    const outputStatesRef = useRef({})

    // Hardware access – used to send the global settingbytes on page load
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)

    const [data, setData] = useState(dataProp || null)
    const [loading, setLoading] = useState(
        !dataProp && subprogramId ? true : false
    )
    const [outputStates, setOutputStates] = useState({})
    const [outputImages, setOutputImages] = useState({})
    const [aiVoiceover, setAiVoiceover] = useState(
        () => sessionStorage.getItem('aiVoiceToggle') !== 'false'
    )

    const [showSubtitleDisplay, setShowSubtitleDisplay] = useState(false)
    const [subtitleVisible, setSubtitleVisible] = useState(false)
    const [typewriterText, setTypewriterText] = useState('')
    const subtitleRef = useRef(null)
    const [aiSpeaking, setAiSpeaking] = useState(true)

    const [wires, setWires] = useState([])

    const [userInteractedThisStep, setUserInteractedThisStep] = useState(false)

    const [modelOverrides, setModelOverrides] = useState({})

    // Momentary-button gating: after a tack press we wait for the device's
    // "Rdone" response (\r\nRdone\r\n) before allowing another tack/hold press.
    // This stops spamming while the previous action is still running on the
    // hardware. A safety fallback timer releases the lock if that response
    // never arrives (e.g. device unplugged mid-action).
    // Locked while an action is (believed to be) running on the board.
    const awaitingResponseRef = useRef(false)
    // Timer that lifts the lock ACTION_HOLD_MS after the board's last response.
    const releaseTimerRef = useRef(null)
    // Timestamp of the last tack press, used to decide whether a "busy" reply
    // is a response to our command (→ re-lock) or unrelated chatter.
    const lastPressAtRef = useRef(0)
    // Mapping (id) of the tack button currently mid-action, so it can be
    // returned to its OFF state once the action completes.
    const activeTackMappingRef = useRef(null)
    const noticeTimerRef = useRef(null)
    // Text of the bottom notice ('' = hidden). Reused for the "please wait" and
    // "please connect the device" messages.
    const [noticeText, setNoticeText] = useState('')

    // --- NEW: Track if the screen is in portrait mode ---
    // const [isPortrait, setIsPortrait] = useState(
    //     window.innerHeight * 1.5 >= window.innerWidth
    // )

    // --- NEW: Check User Agent for mobile and track portrait mode ---
    const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        )

    const [isMobilePortrait, setIsMobilePortrait] = useState(
        isMobileDevice && window.innerHeight * 1.4 >= window.innerWidth
    )

    // ===== STEP STATE =====
    const storageKey = `playScreen_step_${projectId}_${screenId || 'default'}`
    const hasSteps = data?.steps && data.steps.length > 0
    const totalSteps = data?.totalSteps || data?.steps?.length || 0

    // Read from sessionStorage on initial load
    const [currentStep, setCurrentStep] = useState(() => {
        if (isPreviewMode) return 1

        // PRIORITY 1: Explicit jump from Timeline
        if (initialStep) {
            sessionStorage.setItem(storageKey, initialStep.toString())
            return initialStep
        }

        // PRIORITY 2: Memory from last visit
        const savedStep = sessionStorage.getItem(storageKey)
        const parsed = parseInt(savedStep, 10)
        return !isNaN(parsed) && parsed > 0 ? parsed : 1
    })

    // Save step to storage whenever it changes
    useEffect(() => {
        sessionStorage.setItem(storageKey, currentStep)
    }, [currentStep, storageKey])

    // Kit Menu play has no final step: if a stale saved step points past the
    // last real step, clamp back so the Congratulations screen never appears.
    useEffect(() => {
        if (hideFinalStep && hasSteps && currentStep > totalSteps) {
            setCurrentStep(totalSteps)
        }
    }, [hideFinalStep, hasSteps, currentStep, totalSteps])

    const currentStepData = useMemo(() => {
        if (!hasSteps) return null

        // Handle the injected Final Step
        if (currentStep > totalSteps) {
            // Kit Menu play skips the "Congratulations" step — stay on the last
            // real step instead of showing the injected completion screen.
            if (hideFinalStep) {
                return (
                    data.steps.find((s) => s.stepNumber === totalSteps) || null
                )
            }
            return {
                title: data.finalStepTitle || 'Tutorial Complete!',
                instruction:
                    data.finalStepInstruction ||
                    'Great job! Click Continue to proceed.',
                validations: [], // No validations needed to pass final step
                visibleComponents:
                    data.steps[totalSteps - 1]?.visibleComponents || [],
                enableComponents:
                    data.steps[totalSteps - 1]?.enableComponents || [],
                highlight: [], // Remove all highlights
            }
        }

        return data.steps.find((s) => s.stepNumber === currentStep) || null
    }, [hasSteps, data, currentStep, totalSteps, hideFinalStep])

    const visibleComponentIds = useMemo(() => {
        if (!hasSteps || !currentStepData) return null
        return new Set(currentStepData.visibleComponents || [])
    }, [hasSteps, currentStepData])

    const enabledComponentIds = useMemo(() => {
        if (!hasSteps || !currentStepData) return null
        return new Set(currentStepData.enableComponents || [])
    }, [hasSteps, currentStepData])

    const highlightedComponentIds = useMemo(() => {
        if (!hasSteps || !currentStepData) return new Set()
        return new Set(currentStepData.highlight || [])
    }, [hasSteps, currentStepData])

    // const NO_WIRE_COMPONENT_IDS = useMemo(() => new Set(['led_output_1']), [])

    const currentInstruction = useMemo(() => {
        if (!hasSteps) return data?.description || ''
        return currentStepData?.instruction || ''
    }, [hasSteps, currentStepData, data])

    // ===== AUDIO =====
    const playAudioForStep = useCallback(
        (stepNumber) => {
            if (!data?.meta?.audioBaseUrl) return
            const audioUrl = `${data.meta.audioBaseUrl}${stepNumber}.mp3`

            // Make mode: play through the shell's persistent, pre-unlocked
            // audio element so playback survives cross-screen navigation.
            if (makeCtx?.playAudio) {
                makeCtx.playAudio(audioUrl)
                return
            }

            if (!audioRef.current) return
            audioRef.current.src = audioUrl
            audioRef.current
                .play()
                .catch((e) => console.log('Playback interrupted:', e))
        },
        [data, makeCtx]
    )

    const handleAiVoiceoverToggle = useCallback(() => {
        const nextState = !aiVoiceover
        setAiVoiceover(nextState)
        sessionStorage.setItem('aiVoiceToggle', String(nextState))

        if (nextState) {
            playAudioForStep(hasSteps ? currentStep : 1)
            setShowSubtitleDisplay(true)
        } else {
            if (makeCtx?.stopAudio) {
                makeCtx.stopAudio()
            } else if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.currentTime = 0
            }
        }
    }, [aiVoiceover, playAudioForStep, hasSteps, currentStep, makeCtx])

    // Load data from backend if in confined play mode
    useEffect(() => {
        // --- NEW: Resize listener to check aspect ratio dynamically ---
        const handleResize = () => {
            setIsMobilePortrait(
                isMobileDevice && window.innerHeight * 1.4 >= window.innerWidth
            )
        }
        window.addEventListener('resize', handleResize)

        if (!dataProp && subprogramId && isConfinedPlay) {
            const confinedPlayScreenId = sessionStorage.getItem('playScreenId')
            let url = `${BACKEND_URL}/api/play/${subprogramId}`
            if (confinedPlayScreenId) url += `/${confinedPlayScreenId}`
            fetch(url)
                .then((res) => res.json())
                .then((playData) => {
                    setData(playData)
                    setLoading(false)
                })
                .catch((err) => {
                    console.error(
                        'Failed to load play data for subprogram:',
                        err
                    )
                    setLoading(false)
                })
        }

        return () => {
            // --- NEW: Cleanup resize listener ---
            window.removeEventListener('resize', handleResize)
        }
    }, [subprogramId, isConfinedPlay, dataProp])

    // Cleanup: clear confined play flags when component unmounts
    useEffect(() => {
        return () => {
            // Always clear the hide-final-step flag so it never leaks into a
            // later PlayScreen opened by a different route (preview, PlayMode…).
            sessionStorage.removeItem('playHideFinalStep')
            if (isConfinedPlay) {
                sessionStorage.removeItem('isConfinedPlay')
                sessionStorage.removeItem('playSubprogramId')
                sessionStorage.removeItem('playScreenId')
            }
        }
    }, [])

    // Send the global settingbytes (Live Start) once when device connects and data is ready.
    // This initialises the board for the whole PlayScreen so each input control
    // doesn't need to rebuild and send its own LS packet independently.
    const settingbytesSentRef = useRef(false)
    useEffect(() => {
        if (
            data?.settingbytes &&
            Array.isArray(data.settingbytes) &&
            data.settingbytes.length > 0 &&
            worker &&
            webSerial?.isConnected &&
            !settingbytesSentRef.current
        ) {
            settingbytesSentRef.current = true
            worker.postMessage({
                type: 'writeArray',
                value: data.settingbytes,
            })
        }
        // Reset so it fires again if device reconnects
        if (!webSerial?.isConnected) {
            settingbytesSentRef.current = false
        }
    }, [data, worker, webSerial])

    // ===== SEND 'PLAY' BYTES ON PAGE / DEVICE LOAD =====
    // Sets the connected hardware into Play Mode on initial render / reconnect
    const hasSentPlayBytesRef = useRef(false)

    useEffect(() => {
        if (worker && webSerial?.isConnected && !hasSentPlayBytesRef.current) {
            hasSentPlayBytesRef.current = true

            // 'WOOF' ASCII codes: W=87, O=79, O=79, F=70
            const playBytes = [
                'W'.charCodeAt(0),
                'O'.charCodeAt(0),
                'O'.charCodeAt(0),
                'F'.charCodeAt(0),
            ]

            // Delay the send: after the page loads it takes ~1-2s for the device
            // to finish connecting before it can actually receive bytes.
            const timer = setTimeout(() => {
                worker.postMessage({
                    type: 'writeArray',
                    value: playBytes,
                })
                console.log('[PlayScreen] Hardware set to PLAY mode:', playBytes)
            }, 3000)

            return () => clearTimeout(timer)
        }

        // Reset ref if device disconnects so 'PLAY' is re-sent on reconnection
        if (!webSerial?.isConnected) {
            hasSentPlayBytesRef.current = false
        }
    }, [worker, webSerial?.isConnected])

    const buildStateUpToStep = (targetStepNumber, allSteps) => {
        let accumulatedState = {}

        // Helper: get all conditions from a step regardless of schema
        const getStepConds = (s) =>
            s.conditionBlocks ? s.conditionBlocks.flat() : s.validations || []

        // PRE-FILL: Scan all steps and initialize every known key to 0.
        // Supports conditionBlocks (new) and validations (legacy).
        allSteps.forEach((step) => {
            getStepConds(step).forEach((cond) => {
                const key = cond.inputId || cond.mapping
                if (key && accumulatedState[key] === undefined) {
                    accumulatedState[key] = 0
                }
            })
        })

        // Loop through all steps up to the target step to build the continuous state
        for (let i = 0; i < targetStepNumber; i++) {
            const step = allSteps[i]
            if (!step) continue
            const conds = getStepConds(step)
            if (!conds.length) continue

            const stepState = {}
            const ranges = {}

            const MOMENTARY_BUTTON_TYPES = [
                'tack_button_control',
                'hold_button_control',
                'trigger_button_control',
            ]

            conds.forEach((cond) => {
                const key = cond.inputId || cond.mapping
                if (!key) return

                // Momentary buttons must never have ON state persisted across
                // steps – they are press-and-hold inputs, not toggles.
                const condType = cond.inputType || cond.type
                if (
                    MOMENTARY_BUTTON_TYPES.includes(condType) ||
                    MOMENTARY_BUTTON_TYPES.some((t) => key.startsWith(t))
                )
                    return

                // NEW schema: has inputId
                if (cond.inputId) {
                    if (cond.expectedStatus !== undefined) {
                        // Button-type condition
                        stepState[key] = cond.expectedStatus === 'ON' ? 100 : 0
                    } else {
                        // Slider / RGB channel condition
                        const { operator, value } = cond
                        if (operator === '==') {
                            stepState[key] = value
                        } else if (operator === '>') {
                            if (value === 0) stepState[key] = 100
                            else
                                ranges[key] = {
                                    ...ranges[key],
                                    min: value + 1,
                                }
                        } else if (operator === '>=') {
                            ranges[key] = { ...ranges[key], min: value }
                        } else if (operator === '<') {
                            ranges[key] = { ...ranges[key], max: value - 1 }
                        } else if (operator === '<=') {
                            ranges[key] = { ...ranges[key], max: value }
                        }
                    }
                    return
                }

                // LEGACY schema: has mapping
                const { operator, value } = cond
                if (operator === '==') {
                    stepState[key] = value
                } else if (operator === '>') {
                    if (value === 0) stepState[key] = 100
                    else ranges[key] = { ...ranges[key], min: value + 1 }
                } else if (operator === '>=') {
                    ranges[key] = { ...ranges[key], min: value }
                } else if (operator === '<') {
                    ranges[key] = { ...ranges[key], max: value - 1 }
                } else if (operator === '<=') {
                    ranges[key] = { ...ranges[key], max: value }
                }
            })

            // Resolve any ranges (like >= 30 and <= 70) to their midpoint
            Object.keys(ranges).forEach((key) => {
                if (stepState[key] !== undefined) return
                const r = ranges[key]
                if (r.min !== undefined && r.max !== undefined) {
                    stepState[key] = Math.floor((r.min + r.max) / 2)
                } else if (r.min !== undefined) {
                    stepState[key] = r.min
                } else if (r.max !== undefined) {
                    stepState[key] = r.max
                }
            })

            accumulatedState = { ...accumulatedState, ...stepState }
        }
        return accumulatedState
    }

    // ===== STEP NAVIGATION =====
    const handleStepNext = useCallback(() => {
        setUserInteractedThisStep(false) // Reset lock

        if (!hasSteps) {
            if (dataProp && !isConfinedPlay) ContentEngine.next()
            return
        }

        if (currentStep <= totalSteps) {
            // Fire hardware bytes for this step's onSuccessActions
            if (worker) {
                ;(currentStepData?.onSuccessActions || []).forEach(
                    async (action) => {
                        // free===true → fixedBytes (recorded), free===false → direct (raw)
                        // Also support legacy field names (savedBytes/rawBytes) for old data
                        const fixedBytes =
                            action.fixedBytes || action.savedBytes || null
                        const directBytes =
                            action.direct || action.rawBytes || null
                        const useFree =
                            action.free !== undefined
                                ? action.free
                                : fixedBytes !== null

                        if (
                            useFree &&
                            fixedBytes &&
                            Array.isArray(fixedBytes)
                        ) {
                            for (const bytes of fixedBytes) {
                                worker.postMessage({
                                    type: 'writeArray',
                                    value: bytes,
                                })
                                await new Promise((r) => setTimeout(r, 100))
                            }
                        } else if (!useFree && directBytes) {
                            if (Array.isArray(directBytes)) {
                                if (
                                    directBytes.length > 0 &&
                                    Array.isArray(directBytes[0])
                                ) {
                                    // New 2D format: iterate packets
                                    for (const packet of directBytes) {
                                        worker.postMessage({
                                            type: 'writeArray',
                                            value: packet,
                                        })
                                        await new Promise((r) =>
                                            setTimeout(r, 100)
                                        )
                                    }
                                } else {
                                    // Legacy 1D format: send as single packet
                                    worker.postMessage({
                                        type: 'writeArray',
                                        value: directBytes,
                                    })
                                }
                            } else if (typeof directBytes === 'string') {
                                // Legacy string format
                                worker.postMessage({
                                    type: 'write',
                                    value: directBytes,
                                })
                            }
                        }
                    }
                )
            }
            const nextStep = currentStep + 1
            // Kit Menu play: the last real step is the end — don't advance into
            // the injected "Congratulations" step.
            if (hideFinalStep && nextStep > totalSteps) {
                setShowSubtitleDisplay(true)
                return
            }
            setCurrentStep(nextStep)
            if (aiVoiceover) playAudioForStep(nextStep)
        } else {
            if (dataProp && !isConfinedPlay) {
                sessionStorage.removeItem(storageKey) // Clear saved step
                ContentEngine.next()
            }
        }
        setShowSubtitleDisplay(true)
    }, [
        hasSteps,
        currentStep,
        totalSteps,
        aiVoiceover,
        playAudioForStep,
        dataProp,
        isConfinedPlay,
        hideFinalStep,
        worker,
        currentStepData,
    ])

    const handleStepPrev = useCallback(() => {
        setUserInteractedThisStep(false)

        if (!hasSteps) {
            if (dataProp && !isConfinedPlay) ContentEngine.prev()
            return
        }

        if (currentStep > 1) {
            const prevStep = currentStep - 1
            // Ensure we don't try to read state from the "bonus" step index
            const targetStateStep =
                prevStep > totalSteps ? totalSteps - 1 : prevStep - 1
            setOutputStates(buildStateUpToStep(targetStateStep, data.steps))
            setCurrentStep(prevStep)
            if (aiVoiceover) playAudioForStep(prevStep)
        } else {
            if (dataProp && !isConfinedPlay) {
                sessionStorage.removeItem(storageKey) // Clear saved step
                ContentEngine.prev()
            }
        }
        setShowSubtitleDisplay(true)
    }, [
        hasSteps,
        currentStep,
        aiVoiceover,
        playAudioForStep,
        dataProp,
        data,
        isConfinedPlay,
    ])

    const resetTutorial = useCallback(() => {
        setUserInteractedThisStep(false)
        setCurrentStep(1)

        setOutputStates(buildStateUpToStep(0, data?.steps || []))

        if (aiVoiceover) playAudioForStep(1)
        setShowSubtitleDisplay(true)
    }, [aiVoiceover, playAudioForStep, data])

    // ===== VALIDATION LOGIC =====
    const evaluateCondition = useCallback(
        (cond) => {
            // NEW schema: uses inputId
            if (cond.inputId) {
                const BUTTON_TYPES = [
                    'button_control',
                    'tack_button_control',
                    'hold_button_control',
                    'trigger_button_control',
                    'rgb_button_control',
                ]

                if (BUTTON_TYPES.includes(cond.inputType)) {
                    const val = outputStates[cond.inputId] || 0
                    const status = val > 0 ? 'ON' : 'OFF'
                    return status === (cond.expectedStatus || 'ON')
                }

                if (cond.inputType === 'rgb_control' && cond.channel) {
                    const channelKey = `${cond.inputId}_${cond.channel}`
                    const channelVal = Number(outputStates[channelKey]) || 0
                    return applyNumericOperator(
                        channelVal,
                        cond.operator || '>',
                        cond.value
                    )
                }

                if (cond.inputType === 'slider_control') {
                    const val = Number(outputStates[cond.inputId]) || 0
                    return applyNumericOperator(
                        val,
                        cond.operator || '>',
                        cond.value
                    )
                }

                return false
            }

            // LEGACY schema: uses mapping string
            if (cond.mapping !== undefined) {
                const currentValue = Number(outputStates[cond.mapping]) || 0
                return applyNumericOperator(
                    currentValue,
                    cond.operator || '>',
                    cond.value
                )
            }

            return false
        },
        [outputStates]
    )

    const isStepValid = useMemo(() => {
        if (!hasSteps || !currentStepData) return true
        // Free step: always valid, user just clicks Next
        if (currentStepData.isFreeStep) return true
        // New schema: conditionBlocks (outer OR, inner AND)
        if (currentStepData.conditionBlocks) {
            if (!currentStepData.conditionBlocks.length) return true
            return currentStepData.conditionBlocks.some((block) =>
                block.every((cond) => evaluateCondition(cond))
            )
        }
        // Legacy schema: flat AND validations
        if (!currentStepData.validations) return true
        return currentStepData.validations.every((cond) =>
            evaluateCondition(cond)
        )
    }, [hasSteps, currentStepData, evaluateCondition])

    // ===== AUTO ADVANCE STEP =====
    useEffect(() => {
        if (!hasSteps || currentStep > totalSteps) return

        // ADD THIS LINE: Prevent auto-advance if it's a free step so they MUST click next
        if (currentStepData?.isFreeStep) return

        // Only auto-advance when there are conditions to evaluate
        const hasConditions =
            currentStepData?.conditionBlocks?.length ||
            currentStepData?.validations?.length
        if (!hasConditions) return

        // The lock: Only auto-advance if the step is valid AND the user actually did something
        if (isStepValid && userInteractedThisStep) {
            const timer = setTimeout(() => {
                handleStepNext()
            }, 200) // so that user can keep the tack button pressed
            return () => clearTimeout(timer)
        }
    }, [
        isStepValid,
        userInteractedThisStep,
        currentStepData,
        hasSteps,
        currentStep,
        totalSteps,
        handleStepNext,
    ])

    // ===== INITIAL LOAD & RESTORE STATE =====
    useEffect(() => {
        if (!loading && data) {
            // Restore interactive states (sliders/buttons) if we reloaded mid-tutorial
            if (currentStep > 1 && data.steps) {
                setOutputStates(buildStateUpToStep(currentStep - 1, data.steps))
            }

            // Seed every output with its defaultImage so the screen starts correctly.
            // The global eval will only override these when an action explicitly fires.
            const seeded = {}
            ;(data.components || []).forEach((comp) => {
                if (comp.type === 'output' || comp.type === 'custom_output') {
                    seeded[comp.id] = comp.properties?.defaultImage || ''
                }
            })
            setOutputImages(seeded)

            // Play audio for the saved step (instead of hardcoded 1)
            if (aiVoiceover) {
                playAudioForStep(currentStep)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, data]) // only on initial load

    // ===== GLOBAL OUTPUT IMAGE EVALUATION =====
    // Runs whenever ANY input state changes. Evaluates ALL inputs' events/conditions
    // with AND gate (requiredConditions), then rebuilds outputImages using specificity
    // (weight = number of requiredConditions). Higher weight wins per output.
    useEffect(() => {
        if (!data?.components) return

        const TOGGLE_BUTTON_TYPES = [
            'button_control',
            'tack_button_control',
            'hold_button_control',
            'rgb_button_control',
        ]
        const TRIGGER_TYPES = ['trigger_button_control']
        const SLIDER_TYPES = ['slider_control', 'rgb_control']

        // outputCandidates tracks the highest-weight winning image per output:
        // { [outputId]: { image, weight } }
        const outputCandidates = {}

        // Evaluate an action's requiredConditions; if met, update the candidate for
        // its outputId only if the new weight beats the current winner.
        // isInactiveFallback=true (onOFF when val=0) forces weight=-1 so any active
        // action with weight>=0 will always override it.
        const applyAction = (action, isInactiveFallback = false) => {
            if (!action.outputId || !action.targetImage) return
            const reqs = action.requiredConditions || []
            if (!reqs.every((req) => evaluateCondition(req))) return
            const weight = isInactiveFallback ? -1 : (action.weight ?? 0)
            const current = outputCandidates[action.outputId]
            if (!current || weight > current.weight) {
                outputCandidates[action.outputId] = {
                    image: action.targetImage,
                    weight,
                }
            }
        }

        data.components.forEach((inputComp) => {
            const { type, properties = {}, id } = inputComp
            const events = properties.events || {}

            if (TOGGLE_BUTTON_TYPES.includes(type)) {
                const val = outputStates[id] || 0
                if (val > 0) {
                    ;(events.onON || []).forEach((action) =>
                        applyAction(action, false)
                    )
                } else {
                    ;(events.onOFF || []).forEach((action) =>
                        applyAction(action, true)
                    )
                }
            } else if (TRIGGER_TYPES.includes(type)) {
                const val = outputStates[id] || 0
                if (val > 0) {
                    ;(events.onTrigger || []).forEach((action) =>
                        applyAction(action, false)
                    )
                }
            } else if (SLIDER_TYPES.includes(type)) {
                ;(properties.conditions || []).forEach((cond) => {
                    let matches = false
                    const channelVal =
                        type === 'rgb_control' && cond.channel
                            ? Number(outputStates[`${id}_${cond.channel}`]) || 0
                            : Number(outputStates[id]) || 0
                    if (cond.type === 'range') {
                        matches =
                            channelVal >= (cond.min ?? 0) &&
                            channelVal <= (cond.max ?? 100)
                    } else {
                        matches = applyNumericOperator(
                            channelVal,
                            cond.operator || '>',
                            cond.value ?? 0
                        )
                    }
                    if (matches) {
                        ;(cond.actions || []).forEach((action) =>
                            applyAction(action, false)
                        )
                    }
                })
            }
        })

        // Extract winning images from candidates.
        // Only update outputs that have an explicit action firing — outputs with no
        // matching action keep their current image (no auto-revert to defaultImage).
        const explicitImages = {}
        Object.entries(outputCandidates).forEach(([outputId, candidate]) => {
            explicitImages[outputId] = candidate.image
        }) // These form the baseline state of the board.

        // Collect onSuccessActions from all steps that have already been passed.
        // These form the baseline state of the board.
        const stepSuccessImages = {}
        if (hasSteps && data.steps) {
            data.steps.forEach((step) => {
                if (step.stepNumber < currentStep) {
                    ;(step.onSuccessActions || []).forEach((a) => {
                        if (a.outputId && a.targetImage) {
                            stepSuccessImages[a.outputId] = a.targetImage
                        }
                    })
                }
            })
        }

        // Priority (lowest → highest):
        //   component interaction images → past-step success images
        // Past-step targetImages must win so that the image set by completing step N
        // persists and is visible when the user enters step N+1, regardless of what
        // the component events (e.g. onOFF) would otherwise show.
        const combined = {
            ...explicitImages,
            ...stepSuccessImages,
        }
        if (Object.keys(combined).length > 0) {
            setOutputImages((prev) => ({ ...prev, ...combined }))
        }
    }, [outputStates, data, evaluateCondition, currentStep, hasSteps])

    // ===== DATA FETCHING =====
    useEffect(() => {
        if (dataProp) {
            setData(dataProp)
            setLoading(false)
            return
        }

        // --- NEW: Intercept unsaved preview data ---
        // Always check sessionStorage first when in preview mode — this handles
        // migrated data previews where the admin stores formData before navigating.
        if (isPreviewMode) {
            const tempData = sessionStorage.getItem('tempPreviewData')
            if (tempData) {
                console.log('Loading unsaved preview data from session storage')
                setData(JSON.parse(tempData))
                setLoading(false)
                return // Exit early so we don't fetch from the backend!
            }
        }

        if (!projectId) {
            setLoading(false)
            return
        }

        async function fetchData() {
            try {
                setLoading(true)

                // Dynamically select Staging or Production API
                let endpoint = isPreviewMode
                    ? `${BACKEND_URL}/api/staging/play/${projectId}`
                    : `${BACKEND_URL}/api/play/${projectId}`
                if (screenId) endpoint += `/${screenId}`

                const res = isPreviewMode
                    ? await fetch(endpoint, { credentials: 'include' })
                    : await fetch(endpoint)
                if (!res.ok) throw new Error('Failed to fetch play screen')
                const json = await res.json()
                setData(json)
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [dataProp, projectId, isPreviewMode])

    // ===== WIRES =====
    const calculateWires = useCallback(() => {
        if (!data || !data.components || !safeAreaRef.current) return

        const containerRect = safeAreaRef.current.getBoundingClientRect()
        const boardComp = data.components.find(
            (c) => c.type === 'toki_board' || c.type === 'board'
        )
        const device = sessionStorage.getItem('allowedDevice') || 'Toki'
        if (!boardComp) return

        const boardEl = document.getElementById(boardComp.id)
        if (!boardEl) return
        const boardRect = boardEl.getBoundingClientRect()

        // --- NEW: Calculate how much the board has scaled ---
        // (300 is the approximate original pixel width of your board image)
        const BASE_BOARD_WIDTH = device === 'Poki' ? 150 : 290 // Adjust if needed
        const boardScale = boardRect.width / BASE_BOARD_WIDTH

        const tokiPortOffsets = {
            M1: [152, 112],
            M3: [178, 112],
            servo_motor1: [147, 71],
            servo_motor2: [183, 71],
            A1: [147, 90],
            A2: [183, 90],
        }

        const pokiPortOffsets = {
            servo_motor1: [33, 7],
            servo_motor2: [115, 7],

            A1: [29, 18],
            A2: [121, 18],

            B1: [33, 77],
            B2: [115, 46],

            C1: [33, 60],
            C2: [33, 68],

            D1: [115, 62],
            D2: [115, 54],

            E: [6, 42],
            E1: [18, 42],
            E2: [29, 42],

            F: [117, 70],
            F1: [131, 70],
            F2: [142, 70],

            M1: [43, 85],
            M3: [105, 85],
        }

        const COMPONENT_OFFSET = [25, 81]
        const newWires = []

        // Only draw wires from board to output / custom_output components
        const shouldWireComponent = (comp) => {
            if (!comp?.port) return false
            if (comp.type !== 'output' && comp.type !== 'custom_output')
                return false

            if (comp.properties?.showWire === true) return true
            if (comp.properties?.showWire === false) return false

            return comp.id !== 'led_output_1'
        }

        const componentsToWire = visibleComponentIds
            ? data.components.filter((c) => visibleComponentIds.has(c.id))
            : data.components

        componentsToWire.forEach((comp) => {
            if (shouldWireComponent(comp)) {
                const compEl = document.getElementById(comp.id)
                if (!compEl) return

                const compRect = compEl.getBoundingClientRect()

                // ... the rest of the existing wire calculation stays exactly the same ...
                let portOffset = [0, 0]
                if (device === 'Toki' && tokiPortOffsets[comp.port]) {
                    portOffset = tokiPortOffsets[comp.port]
                } else if (device === 'Poki' && pokiPortOffsets[comp.port]) {
                    portOffset = pokiPortOffsets[comp.port] // Route to Poki offsets
                } else {
                    portOffset = Ports(comp.port) || [0, 0]
                }

                // --- CHANGED: Multiply port offsets by the scale factor ---
                const startX =
                    boardRect.left -
                    containerRect.left +
                    portOffset[0] * boardScale
                const startY =
                    boardRect.top -
                    containerRect.top +
                    portOffset[1] * boardScale
                let endX =
                    compRect.left - containerRect.left + COMPONENT_OFFSET[0]
                let endY =
                    compRect.top - containerRect.top + COMPONENT_OFFSET[1]

                // =========== do some tweaking to endX and endY if dynamex motor
                let imgSrcToRender = resolveStateImage(
                    comp.properties.states,
                    comp.properties.defaultImage
                )
                if (!imgSrcToRender) imgSrcToRender = comp.properties.offImage
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'dynamexMotor@2x.png'
                ) {
                    endX += 10
                    endY -= 16
                }
                // ============= for ultrasonic sensor
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'ultrasonicToki@2x.png'
                ) {
                    endX += 10
                    endY -= 36
                }
                // ============= for dc motor
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'dcmotor.png'
                ) {
                    endY -= 6
                }
                // ============= for OLED
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'tokiOled@2x.png'
                ) {
                    endY -= 38
                    endX += 36
                }
                // ============= for IMU
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'tokiOled@2x.png'
                ) {
                    endY -= 6
                }
                // ============= for color_sensor
                if (
                    (comp.type === 'output' || comp.type === 'custom_output') &&
                    imgSrcToRender === 'colorSensor@2x.png'
                ) {
                    endY -= 6
                }
                // ========================================

                const controlPointYOffset = Math.abs(endY - startY) * 0.5
                const cp1X = startX
                const cp1Y = startY + controlPointYOffset + 50
                const cp2X = endX
                const cp2Y = endY + controlPointYOffset + 60

                const pathString = `M ${startX} ${startY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${endX} ${endY}`

                newWires.push({
                    id: `${boardComp.id}-to-${comp.id}`,
                    path: pathString,
                })
            }
        })
        setWires(newWires)
    }, [data, visibleComponentIds]) // Removed NO_WIRE_COMPONENT_IDS from dependencies

    useEffect(() => {
        // Recalculate wires when the window is resized or zoomed
        window.addEventListener('resize', calculateWires)

        // Cleanup listener on unmount
        return () => {
            window.removeEventListener('resize', calculateWires)
        }
    }, [calculateWires])

    // ===== MODEL ANIMATIONS (Input-Driven) =====
    useEffect(() => {
        if (!data?.components) return

        const TOGGLE_BUTTON_TYPES = [
            'button_control',
            'tack_button_control',
            'hold_button_control',
            'rgb_button_control',
        ]
        const TRIGGER_TYPES = ['trigger_button_control']
        const SLIDER_TYPES = ['slider_control', 'rgb_control']

        const resolveModelDuration = (mv) => {
            const direct = Number(mv?.duration) || 0
            if (direct > 0) return direct
            return Number(mv?.model?.animations?.[0]?.duration) || 0
        }

        const modelComps = data.components.filter((c) => c.type === 'model')
        const getModelComp = (id) => modelComps.find((m) => m.id === id)

        const execModelAction = (ma) => {
            const mv = modelRefs.current[ma.targetModelId]
            if (!mv) return
            const { action } = ma
            if (action === 'switchModelAnim') {
                const mc = getModelComp(ma.targetModelId)
                setModelOverrides((prev) => ({
                    ...prev,
                    [ma.targetModelId]: {
                        modelUrl: ma.targetModelUrl || mc?.modelUrl,
                        animationName:
                            ma.targetAnimation ||
                            currentStepData?.models?.animationName,
                        cameraOrbit: ma.targetOrbit || mc?.cameraOrbit,
                    },
                }))
                return
            }
            let duration = resolveModelDuration(mv)
            if (duration <= 0) {
                mv.play?.()
                mv.pause?.()
                duration = resolveModelDuration(mv)
            }
            if (action === 'play') mv.play?.()
            if (action === 'pause') mv.pause?.()
            if (action === 'togglePlay') {
                mv.paused ? mv.play?.() : mv.pause?.()
            }
            if (action === 'jumpTo' && duration > 0) {
                mv.pause?.()
                mv.currentTime = (ma.targetFrame ?? 0) * duration
            }
            if (action === 'glideToFrame' && duration > 0) {
                mv.pause?.()
                const startT = (ma.startFrame ?? 0) * duration
                const endT = (ma.endFrame ?? 1) * duration
                const durationMs = Math.max(100, 5000 - (ma.speed ?? 50) * 45)
                const startTime = performance.now()
                if (mv._glideRaf) cancelAnimationFrame(mv._glideRaf)
                const animate = (now) => {
                    const progress = Math.min((now - startTime) / durationMs, 1)
                    mv.currentTime = startT + (endT - startT) * progress
                    if (progress < 1)
                        mv._glideRaf = requestAnimationFrame(animate)
                }
                mv._glideRaf = requestAnimationFrame(animate)
            }
        }

        const revertSwitchModelAnim = (modelId) => {
            setModelOverrides((prev) => {
                const n = { ...prev }
                delete n[modelId]
                return n
            })
        }

        data.components.forEach((inputComp) => {
            const { type, properties = {}, id } = inputComp
            const modelActions = properties.modelActions || {}

            if (TOGGLE_BUTTON_TYPES.includes(type)) {
                const cur = outputStates[id] || 0
                const prev = prevOutputs.current[id] || 0
                if (cur === prev) return
                if (prev === 0 && cur > 0) {
                    ;(modelActions.onON || []).forEach(execModelAction)
                } else if (prev > 0 && cur === 0) {
                    ;(modelActions.onOFF || []).forEach((ma) => {
                        if (ma.action === 'switchModelAnim')
                            revertSwitchModelAnim(ma.targetModelId)
                        else execModelAction(ma)
                    })
                }
            } else if (TRIGGER_TYPES.includes(type)) {
                const cur = outputStates[id] || 0
                const prev = prevOutputs.current[id] || 0
                if (cur === prev) return
                if (prev === 0 && cur > 0) {
                    ;(modelActions.onTrigger || []).forEach(execModelAction)
                }
            } else if (SLIDER_TYPES.includes(type)) {
                const isRgb = type === 'rgb_control'
                ;(Array.isArray(properties.modelActions)
                    ? properties.modelActions
                    : []
                ).forEach((ma) => {
                    const stateKey =
                        isRgb && ma.channel ? `${id}_${ma.channel}` : id
                    const cur = Number(outputStates[stateKey]) || 0
                    const prev = Number(prevOutputs.current[stateKey]) || 0
                    if (cur === prev) return

                    const mv = modelRefs.current[ma.targetModelId]
                    if (!mv) return
                    let duration = resolveModelDuration(mv)
                    if (duration <= 0) {
                        mv.play?.()
                        mv.pause?.()
                        duration = resolveModelDuration(mv)
                    }

                    if (ma.type === 'scrub') {
                        mv.pause?.()
                        const linkedSlider = data.components.find(
                            (c) => c.id === id
                        )
                        const minIn = linkedSlider?.properties?.min ?? 0
                        const maxIn = linkedSlider?.properties?.max ?? 100
                        const clamped = Math.max(minIn, Math.min(maxIn, cur))
                        const normalized =
                            maxIn === minIn
                                ? 0
                                : (clamped - minIn) / (maxIn - minIn)
                        const startFrame = ma.startFrame ?? 0
                        const endFrame = ma.endFrame ?? 1
                        const targetPercent =
                            startFrame + normalized * (endFrame - startFrame)
                        if (duration > 0)
                            mv.currentTime = targetPercent * duration
                    } else if (ma.type === 'trigger' || !ma.type) {
                        const prevMet = applyNumericOperator(
                            prev,
                            ma.operator || '>',
                            ma.value ?? 0
                        )
                        const nowMet = applyNumericOperator(
                            cur,
                            ma.operator || '>',
                            ma.value ?? 0
                        )
                        if (!prevMet && nowMet) execModelAction(ma)
                    }
                })
            }
        })

        prevOutputs.current = { ...outputStates }
    }, [outputStates, data])

    // Keep outputStatesRef in sync so the seeding effect below always has a fresh snapshot.
    useEffect(() => {
        outputStatesRef.current = outputStates
    }, [outputStates])

    // ===== SEED prevLiveStatesRef on live step entry =====
    // Without seeding, every live item appears "changed" on the FIRST interaction
    // (prevRef[key] === undefined vs current value), which causes ALL items to send
    // bytes simultaneously — including rgb_control which fires with its stale
    // accumulated value (e.g. 100,100,100) even though only a button was clicked.
    useEffect(() => {
        // Clear the ref whenever we move to a new step regardless of type.
        prevLiveStatesRef.current = {}
        if (!currentStepData?.isLiveStep) return
        const liveItems = normalizeLive(currentStepData?.live)
        if (Object.keys(liveItems).length === 0) return
        // Snapshot current outputStates as the baseline so only real changes fire bytes.
        const snap = outputStatesRef.current
        Object.entries(liveItems).forEach(([inputId]) => {
            const inputComp = data?.components?.find((c) => c.id === inputId)
            if (!inputComp) return
            if (inputComp.type === 'rgb_control') {
                prevLiveStatesRef.current[`${inputId}_red`] =
                    Number(snap[`${inputId}_red`]) || 0
                prevLiveStatesRef.current[`${inputId}_green`] =
                    Number(snap[`${inputId}_green`]) || 0
                prevLiveStatesRef.current[`${inputId}_blue`] =
                    Number(snap[`${inputId}_blue`]) || 0
            } else {
                prevLiveStatesRef.current[inputId] = snap[inputId] || 0
            }
        })
    }, [currentStepData, data])

    // ===== LIVE STEP – send hardware bytes ONLY on input state change =====
    useEffect(() => {
        if (!worker || !webSerial?.isConnected) return
        if (!currentStepData?.isLiveStep) return
        const liveItems = normalizeLive(currentStepData?.live)
        if (Object.keys(liveItems).length === 0) return

        // Helper: transform a raw UI value to its hardware-domain byte value.
        const applyHwTransform = (rawVal, compName) => {
            const ct = compName || ''
            if (ct === 'servo_motor_270' || ct === 'servo_motor_sg90')
                return Math.round(0.6667 * rawVal)
            if (ct === 'servo_motor_360') return 100 + rawVal
            if (
                ct === 'dynamex_motor' ||
                ct === 'dc_motor' ||
                ct === 'geared_motor' ||
                ct === 'mini_geared_motor'
            ) {
                const scale = ct === 'dynamex_motor' ? 0.8 : 1.0
                return Math.round(Math.abs(rawVal) * scale)
            }
            return rawVal
        }

        Object.entries(liveItems).forEach(([inputId, liveItem]) => {
            const {
                index,
                fixedBytes,
                onValue,
                isFree,
                compsIds,
                extraTargets,
            } = liveItem
            if (!fixedBytes || !inputId) return

            const inputComp = data?.components?.find((c) => c.id === inputId)
            if (!inputComp) return

            const inputType = inputComp.type
            let hasChanged = false

            // 1. Check if this specific component's state actually changed
            if (inputType === 'rgb_control') {
                const rKey = `${inputId}_red`
                const gKey = `${inputId}_green`
                const bKey = `${inputId}_blue`

                const rVal = Number(outputStates[rKey]) || 0
                const gVal = Number(outputStates[gKey]) || 0
                const bVal = Number(outputStates[bKey]) || 0

                if (
                    prevLiveStatesRef.current[rKey] !== rVal ||
                    prevLiveStatesRef.current[gKey] !== gVal ||
                    prevLiveStatesRef.current[bKey] !== bVal
                ) {
                    hasChanged = true
                    prevLiveStatesRef.current[rKey] = rVal
                    prevLiveStatesRef.current[gKey] = gVal
                    prevLiveStatesRef.current[bKey] = bVal
                }
            } else {
                const val = outputStates[inputId] || 0
                if (prevLiveStatesRef.current[inputId] !== val) {
                    hasChanged = true
                    prevLiveStatesRef.current[inputId] = val
                }
            }

            // If it didn't change, skip sending bytes so we don't overwrite other active controls!
            if (!hasChanged) return

            // 2. Format and send the bytes
            const bytes = [...fixedBytes]

            if (compsIds) {
                // ── New Architecture ──────────────────────────────────────────
                // isFree=true → send fixedBytes as-is; isFree=false → inject dynamic values.
                if (!isFree) {
                    Object.entries(compsIds).forEach(
                        ([fetchCompId, targetIndices]) => {
                            const fetchComp = data?.components?.find(
                                (c) => c.id === fetchCompId
                            )
                            if (!fetchComp) return

                            const fetchType = fetchComp.type
                            const fetchName =
                                fetchComp.name
                                    ?.trim()
                                    .toLowerCase()
                                    .replace(/ /g, '_') || ''

                            if (fetchType === 'rgb_control') {
                                // Three-channel injection; index -1 means skip that channel.
                                const rVal =
                                    Number(
                                        outputStates[`${fetchCompId}_red`]
                                    ) || 0
                                const gVal =
                                    Number(
                                        outputStates[`${fetchCompId}_green`]
                                    ) || 0
                                const bVal =
                                    Number(
                                        outputStates[`${fetchCompId}_blue`]
                                    ) || 0
                                const channels = [rVal, gVal, bVal]
                                channels.forEach((chVal, chIdx) => {
                                    const byteIdx = targetIndices[chIdx]
                                    if (
                                        byteIdx !== undefined &&
                                        byteIdx !== -1 &&
                                        byteIdx < bytes.length
                                    ) {
                                        bytes[byteIdx] = chVal
                                    }
                                })
                            } else {
                                // Standard component (slider / button).
                                const byteIdx = targetIndices[0]
                                if (
                                    byteIdx === undefined ||
                                    byteIdx === -1 ||
                                    byteIdx >= bytes.length
                                )
                                    return

                                const BUTTON_TYPES = [
                                    'button_control',
                                    'tack_button_control',
                                    'hold_button_control',
                                    'trigger_button_control',
                                ]
                                if (BUTTON_TYPES.includes(fetchType)) {
                                    const isOn =
                                        (outputStates[fetchCompId] || 0) > 0
                                    const offVal =
                                        fetchName === 'servo_motor_360'
                                            ? 100
                                            : 0
                                    const onVal =
                                        fetchName === 'servo_motor_360'
                                            ? 200
                                            : 100
                                    bytes[byteIdx] = isOn ? onVal : offVal
                                } else {
                                    // slider_control
                                    const rawVal =
                                        Number(outputStates[fetchCompId]) || 0
                                    bytes[byteIdx] = applyHwTransform(
                                        rawVal,
                                        fetchName
                                    )
                                }
                            }
                        }
                    )
                }
                // isFree=true: bytes already contains fixedBytes unchanged.
            } else if (index) {
                // ── Legacy Backward-Compatibility ─────────────────────────────
                if (
                    inputType === 'button_control' ||
                    inputType === 'tack_button_control' ||
                    inputType === 'hold_button_control' ||
                    inputType === 'trigger_button_control'
                ) {
                    const val = outputStates[inputId] || 0
                    const isOn = val > 0
                    if (typeof onValue === 'object' && onValue !== null) {
                        // RGB button ON/OFF
                        bytes[index[0]] = isOn ? (onValue.r ?? 0) : 0
                        bytes[index[1]] = isOn ? (onValue.g ?? 0) : 0
                        bytes[index[2]] = isOn ? (onValue.b ?? 0) : 0
                    } else {
                        // Standard button ON/OFF
                        const ct = liveItem.compType || ''
                        const offVal = ct === 'servo_motor_360' ? 100 : 0
                        bytes[index[0]] = isOn
                            ? (onValue ?? liveItem.max ?? 100)
                            : offVal
                    }
                } else if (inputType === 'slider_control') {
                    let sliderVal = Number(outputStates[inputId]) || 0
                    const ct = liveItem.compType || ''
                    if (ct === 'servo_motor_270' || ct === 'servo_motor_sg90') {
                        sliderVal = Math.round(0.6667 * sliderVal)
                    } else if (ct === 'servo_motor_360') {
                        sliderVal = 100 + sliderVal
                    } else if (
                        ct === 'dynamex_motor' ||
                        ct === 'dc_motor' ||
                        ct === 'geared_motor' ||
                        ct === 'mini_geared_motor'
                    ) {
                        const scale = ct === 'dynamex_motor' ? 0.8 : 1.0
                        sliderVal = Math.round(Math.abs(sliderVal) * scale)
                    }
                    bytes[index[0]] = sliderVal
                } else if (inputType === 'rgb_control') {
                    bytes[index[0]] =
                        Number(outputStates[`${inputId}_red`]) || 0
                    bytes[index[1]] =
                        Number(outputStates[`${inputId}_green`]) || 0
                    bytes[index[2]] =
                        Number(outputStates[`${inputId}_blue`]) || 0
                } else {
                    return
                }
            } else {
                return
            }

            worker.postMessage({ type: 'writeArray', value: bytes })

            // ── Drive any additional target ports from this same input ────────
            // Lets a single input (e.g. one push button) drive several outputs at
            // once, each at its OWN configured value. When the input is active
            // (state != 0, e.g. button pressed) each target fires at its value;
            // when inactive (released / zero) it fires OFF. Non-RGB only (RGB
            // uses an 8-byte packet with a different layout).
            if (
                Array.isArray(extraTargets) &&
                extraTargets.length > 0 &&
                fixedBytes.length !== 8
            ) {
                const inputActive =
                    inputType === 'rgb_control'
                        ? false
                        : (Number(outputStates[inputId]) || 0) !== 0
                extraTargets.forEach((t) => {
                    if (!t) return
                    const portNum =
                        typeof t.portNum === 'number'
                            ? t.portNum
                            : typeof t.port === 'number'
                              ? t.port
                              : null
                    if (portNum === null) return
                    const targetName =
                        t.component?.trim().toLowerCase().replace(/ /g, '_') ||
                        ''
                    // OFF for a 360 servo is neutral (100), everything else is 0.
                    const offVal = targetName === 'servo_motor_360' ? 100 : 0
                    const rawVal = inputActive
                        ? applyHwTransform(Number(t.value) || 0, targetName)
                        : offVal
                    const valueByte = Math.max(
                        0,
                        Math.min(255, Math.round(rawVal))
                    )
                    worker.postMessage({
                        type: 'writeArray',
                        value: [76, 87, t.char ?? 80, portNum, valueByte],
                    })
                })
            }
        })
    }, [outputStates, currentStepData, worker, webSerial, data])

    // ===== TYPEWRITER EFFECT =====
    useEffect(() => {
        if (aiVoiceover && currentInstruction) {
            setTypewriterText('')
            setAiSpeaking(true)
            let i = 0
            const text = currentInstruction
            const interval = setInterval(() => {
                i++
                setTypewriterText(text.slice(0, i))
                if (i >= text.length) {
                    clearInterval(interval)
                    setAiSpeaking(true)
                }
            }, 40)
            return () => clearInterval(interval)
        } else {
            setTypewriterText(currentInstruction || '')
            setAiSpeaking(false)
        }
    }, [aiVoiceover, currentInstruction])

    useEffect(() => {
        const isPlayInteractableClick = (target) => {
            if (!(target instanceof Element)) return false
            return Boolean(
                target.closest(
                    '.playscreen-action-button, .slider-wrapper, .rgb-control-wrapper'
                )
            )
        }

        const handleClick = (e) => {
            // Do not collapse subtitle when user clicks play interactables
            if (isPlayInteractableClick(e.target)) return

            if (
                subtitleRef.current &&
                !subtitleRef.current.contains(e.target)
            ) {
                setShowSubtitleDisplay(false)
                setAiSpeaking(true)
            }
        }

        if (showSubtitleDisplay)
            document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [showSubtitleDisplay])

    // Slide in subtitle on mount
    useEffect(() => {
        const t = setTimeout(() => {
            setSubtitleVisible(true)
            setShowSubtitleDisplay(true)
        }, 50)
        return () => clearTimeout(t)
    }, [])

    useEffect(() => {
        const timer = setTimeout(calculateWires, 200)
        return () => clearTimeout(timer)
    }, [currentStep, calculateWires])

    const goBack = useCallback(() => history.goBack(), [history])

    const handleFullscreen = useCallback(() => {
        // In Make mode, fullscreen the parent wrapper so it persists across screen transitions
        const el = isMake
            ? document.querySelector('.make-page')
            : appContainerRef.current
        const isFullscreen =
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.msFullscreenElement
        if (!isFullscreen) {
            if (el?.requestFullscreen) el.requestFullscreen()
            else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen()
            else if (el?.msRequestFullscreen) el.msRequestFullscreen()
        } else {
            if (document.exitFullscreen) document.exitFullscreen()
            else if (document.webkitExitFullscreen)
                document.webkitExitFullscreen()
            else if (document.msExitFullscreen) document.msExitFullscreen()
        }
    }, [isMake])

    const resolveImage = (imgValue) => {
        if (!imgValue)
            return 'https://cdn-icons-png.flaticon.com/512/863/863268.png'

        if (typeof imgValue === 'string') {
            // Regex updated to handle BOTH single and double quotes: /['"]([^'"]+)['"]/

            // 1. Handle Legacy Hardcoded Project Images
            if (imgValue.includes('renderPrgImage')) {
                const match = imgValue.match(/['"]([^'"]+)['"]/)
                console.log(
                    'Resolving renderPrgImage for:',
                    imgValue,
                    '=>',
                    match ? match[1] : 'No match'
                )
                return match ? renderPrgImage(match[1]) : imgValue
            } else if (imgValue.includes('renderImage')) {
                const match = imgValue.match(/['"]([^'"]+)['"]/)
                return match ? renderImage(match[1]) : imgValue
            } else if (imgValue.includes('renderCompImg')) {
                const match = imgValue.match(/['"]([^'"]+)['"]/)
                return match ? renderCompImg(match[1]) : imgValue
            }
            // 2. Handle External URLs or Base64 Data
            else if (
                imgValue.startsWith('http') ||
                imgValue.startsWith('data:') ||
                imgValue.startsWith('blob:')
            ) {
                return imgValue
            }
            // 3. Handle Backend Uploaded Images
            else if (!imgValue.startsWith('/')) {
                try {
                    return getBackendImage(imgValue)
                } catch (e) {
                    console.error('Failed to load backend image:', e)
                    return `${BACKEND_URL}/static/images/${imgValue}`
                }
            }
        }

        return imgValue
    }

    const resolveModelUrl = (url) => {
        if (!url) return ''
        let finalUrl = url
        const metaModel = Array.isArray(data?.meta?.modelPath)
            ? data.meta.modelPath.find(
                  (entry) => typeof entry === 'string' && entry.trim()
              ) || ''
            : data?.meta?.modelPath || ''

        // 1. If it's already a full web URL or data blob, return it directly
        if (
            url.startsWith('http') ||
            url.startsWith('data:') ||
            url.startsWith('blob:')
        ) {
            finalUrl = url
        }
        // 2. If it's a legacy relative path (e.g. /models/...) and we have a GCS URL in meta, prefer that
        else if (url.startsWith('/models/') || !url.startsWith('http')) {
            if (metaModel && metaModel.startsWith('http')) {
                // Use the GCS URL from project_map instead of the stale local path
                finalUrl = metaModel
            } else if (url.startsWith('/models/')) {
                finalUrl = `${BACKEND_URL}/static${url}`
            } else {
                finalUrl = `${BACKEND_URL}/static/models/${url}`
            }
        }

        console.log(`[PlayScreen] Resolved Model URL:`, finalUrl)
        return finalUrl
    }

    // Gate for momentary (tack / hold) button presses. A tack press starts
    // waiting for the device's "Rdone" response; any tack/hold press while we
    // are still waiting is rejected and a "please wait" notice is shown.
    // Returns true if the press is allowed.
    // Briefly surface the bottom notice with the given text (auto-hides).
    const showNotice = useCallback((text) => {
        setNoticeText(text)
        if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
        noticeTimerRef.current = setTimeout(() => setNoticeText(''), 2000)
    }, [])

    // Lock the momentary buttons and (re)arm the release timer. Called on a tack
    // press and refreshed on every board response for the running action, so the
    // lock outlives the (unknown-length) action and lifts once the board goes
    // quiet for ACTION_HOLD_MS. When it finally releases, the active tack button
    // is returned to its OFF state (visual "action finished").
    const holdMomentaryLock = useCallback(() => {
        awaitingResponseRef.current = true
        if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current)
        releaseTimerRef.current = setTimeout(() => {
            console.log('[TACK-GATE] RELEASED — buttons clickable again')
            awaitingResponseRef.current = false
            releaseTimerRef.current = null
            setNoticeText('')
            if (activeTackMappingRef.current != null) {
                const mapping = activeTackMappingRef.current
                activeTackMappingRef.current = null
                setOutputStates((prev) => ({ ...prev, [mapping]: 0 }))
            }
        }, ACTION_HOLD_MS)
    }, [])

    // Gate for momentary (tack / hold) button presses. While the board is
    // running an action, presses are rejected and a "please wait" notice shows.
    const requestMomentaryPress = useCallback(
        (kind, mapping) => {
            // A device must be connected (same signal as the header bluetooth
            // button) before a tack/hold action can run.
            if (!webSerial?.isConnected) {
                console.log(`[TACK-GATE] ${kind} press ignored — not connected`)
                showNotice('Please connect the device first')
                return false
            }
            if (awaitingResponseRef.current) {
                console.log(`[TACK-GATE] BLOCKED ${kind} press — action running`)
                showNotice('Please wait for the action to complete')
                return false
            }
            if (kind === 'tack') {
                console.log('[TACK-GATE] ARMED on tack press')
                lastPressAtRef.current = Date.now()
                // Remember which tack is running so it can be switched OFF once
                // the action completes (see the release timer above).
                activeTackMappingRef.current = mapping ?? null
                holdMomentaryLock()
            }
            return true
        },
        [showNotice, holdMomentaryLock, webSerial?.isConnected]
    )

    // Listen for the device's "Rdone" response (\r\nRdone\r\n) — it means the
    // last tack action finished, so release the momentary-button lock. The
    // worker forwards every incoming serial line as a 'read' / 'actionRead'
    // message whose stringData contains the decoded text.
    useEffect(() => {
        if (!worker) return
        const handleWorkerMessage = (e) => {
            const msg = e.data || {}
            let str = ''
            if (msg.type === 'read') str = msg.value
            else if (msg.type === 'actionRead') str = msg.value?.stringData
            else return
            if (typeof str !== 'string') return

            const hasRdone = str.includes('Rdone')
            const hasBusy = str.includes('busy')
            if (!hasRdone && !hasBusy) return

            console.log(
                `[TACK-GATE] serial=${JSON.stringify(str)} ` +
                    `Rdone=${hasRdone} busy=${hasBusy} locked=${awaitingResponseRef.current}`
            )

            if (awaitingResponseRef.current) {
                // The board is actively working on our action (it either just
                // started it → "Rdone", or rejected an extra command → "busy").
                // Either way keep the lock alive past the board's last response.
                holdMomentaryLock()
                if (hasBusy)
                    showNotice('Please wait for the action to complete')
            } else if (
                hasBusy &&
                Date.now() - lastPressAtRef.current < PRESS_RELEVANCE_MS
            ) {
                // The hold expired but the board is still busy with a recent
                // action — a slipped press revealed it. Re-lock and warn.
                console.log('[TACK-GATE] busy after release → re-locking')
                holdMomentaryLock()
                showNotice('Please wait for the action to complete')
            }
        }
        worker.addEventListener('message', handleWorkerMessage)
        return () => worker.removeEventListener('message', handleWorkerMessage)
    }, [worker, holdMomentaryLock, showNotice])

    // Clear pending timers on unmount.
    useEffect(() => {
        return () => {
            if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
            if (releaseTimerRef.current)
                clearTimeout(releaseTimerRef.current)
        }
    }, [])

    const handleControlChange = (mapping, val) => {
        setUserInteractedThisStep(true) // Unlock auto-advance

        // Compute the effective numeric value (handles 'clicked' toggle)
        const currentRaw = outputStates[mapping] || 0
        const effectiveVal =
            val === 'clicked' ? (currentRaw > 0 ? 0 : 100) : Number(val)

        // Keep outputStates in sync; the global eval useEffect rebuilds outputImages
        setOutputStates((prev) => ({ ...prev, [mapping]: effectiveVal }))
    }

    const generateSliderProps = (mapping, compName) => {
        const val = outputStates[mapping] || 0
        const setVal = (v) => handleControlChange(mapping, v)

        const baseProps = {
            setL_Green: setVal,
            isL_Green: val,
            setL_Blue: setVal,
            isL_Blue: val,
            setL_Red: setVal,
            isL_Red: val,
            setR_Green: setVal,
            isR_Green: val,
            setR_Blue: setVal,
            isR_Blue: val,
            setR_Red: setVal,
            isR_Red: val,
            setFreq: setVal,
            isFreq: val,
            value: val,
            val: val,
        }

        if (compName) {
            baseProps[`is${compName}`] = val
            baseProps[`set${compName}`] = setVal
        }

        return baseProps
    }

    const isHighlighted = (compId) => highlightedComponentIds.has(compId)
    const highlightClass = (compId) =>
        isHighlighted(compId) ? ' play-highlight' : ''

    // Helper: find the best matching state image by checking most-specific states first.
    // States with more conditions are more specific and should be evaluated before broad ones.
    const resolveStateImage = (states, defaultImg) => {
        if (!states || !Array.isArray(states)) return defaultImg
        // Sort by specificity: more condition blocks (or more conditions in first block) = higher priority
        const sorted = [...states].sort((a, b) => {
            const aLen = a.conditionBlocks
                ? a.conditionBlocks.reduce((s, blk) => s + blk.length, 0)
                : a.conditions?.length || 0
            const bLen = b.conditionBlocks
                ? b.conditionBlocks.reduce((s, blk) => s + blk.length, 0)
                : b.conditions?.length || 0
            return bLen - aLen
        })
        for (const state of sorted) {
            let conditionsMet
            if (state.conditionBlocks) {
                // New schema: OR across blocks, AND within each block
                conditionsMet = state.conditionBlocks.some((block) =>
                    block.every((cond) => evaluateCondition(cond))
                )
            } else {
                // Legacy schema: flat AND conditions
                conditionsMet = (state.conditions || []).every((cond) =>
                    evaluateCondition(cond)
                )
            }
            if (conditionsMet) return state.image
        }
        return defaultImg
    }

    // --- Shell: stable onStepClick for header timeline nav ---
    const onStepClick = useCallback(
        (stepNum) => {
            setUserInteractedThisStep(false)
            setOutputStates(buildStateUpToStep(stepNum - 1, data?.steps || []))
            setCurrentStep(stepNum)
            if (aiVoiceover) playAudioForStep(stepNum)
            setShowSubtitleDisplay(true)
        },
        [aiVoiceover, playAudioForStep, data]
    )

    // --- Shell: register header props ---
    useEffect(() => {
        if (!setHeaderProps) return
        setHeaderProps({
            title: data?.title || 'Play Mode',
            titlePosition: 'left',
            help_btnPosition: 'right',
            isGoBack: true,
            goBack,
            showHelp: true,
            showBluetoothBtn: true,
            currentStep: hasSteps
                ? Math.min(currentStep, totalSteps)
                : undefined,
            totalSteps: hasSteps ? totalSteps : undefined,
            stepTitles: hasSteps
                ? data?.steps?.map((s) => s?.title || '') || []
                : [],
            onStepClick,
        })
    }, [
        setHeaderProps,
        data,
        currentStep,
        totalSteps,
        hasSteps,
        goBack,
        onStepClick,
    ])

    // --- Shell: register footer props ---
    useEffect(() => {
        if (!setFooterProps) return
        const isFinalStep = hasSteps && currentStep > totalSteps
        const isFirstStep = !hasSteps || currentStep <= 1
        const stepCounter = hasSteps
            ? isFinalStep
                ? 'Complete'
                : `Step ${currentStep} of ${totalSteps}`
            : ''
        const stepTitle = hasSteps
            ? currentStepData?.title || ''
            : data?.title || 'Play Mode'
        const nextDisabled = (isFinalStep && !dataProp) || !isStepValid
        const nextClassName = currentStepData?.isFreeStep
            ? 'free-step-highlight'
            : ''
        setFooterProps({
            stepCounter,
            stepTitle,
            prevDisabled: isFirstStep && !dataProp,
            nextDisabled,
            nextLabel: isFinalStep ? 'Continue' : 'Next',
            nextClassName,
            showNextTooltip: !isStepValid,
            nextTooltipText: 'Complete the action to proceed',
            onPrev: handleStepPrev,
            onNext: handleStepNext,
        })
    }, [
        setFooterProps,
        hasSteps,
        currentStep,
        totalSteps,
        currentStepData,
        dataProp,
        isStepValid,
        handleStepPrev,
        handleStepNext,
        data,
    ])

    // --- Shell: register toolbar props ---
    useEffect(() => {
        if (!setToolbarProps) return
        setToolbarProps({
            aiVoiceover,
            onAiToggle: () => {
                handleAiVoiceoverToggle()
                setSubtitleVisible(!aiVoiceover)
                setShowSubtitleDisplay(!aiVoiceover)
            },
            onFullscreen: handleFullscreen,
            onReset: resetTutorial,
        })
    }, [
        setToolbarProps,
        aiVoiceover,
        handleAiVoiceoverToggle,
        handleFullscreen,
        resetTutorial,
    ])

    const renderComponent = (comp) => {
        const isVisible =
            !visibleComponentIds || visibleComponentIds.has(comp.id)

        // 1. Interactive UI disabling logic
        // Only components capable of interaction (sliders, buttons) will be blocked.
        const isInteractiveType = [
            'button_control',
            'tack_button_control',
            'hold_button_control',
            'trigger_button_control', // Included trigger explicitly
            'slider_control',
            'custom_slider_control',
            'rgb_control',
            'rgb_button_control',
        ].includes(comp.type)

        // Backwards compatibility fallback: if the 'enableComponents' array doesn't exist on an old saved step, fall back to the old highlight logic.
        const hasEnableArray =
            currentStepData && currentStepData.enableComponents !== undefined

        const isInteractable = hasEnableArray
            ? enabledComponentIds.has(comp.id)
            : !hasSteps ||
              highlightedComponentIds.size === 0 ||
              isHighlighted(comp.id)

        // Apply pointerEvents: 'none' and visual dimming to ALL disabled components consistently.
        const interactiveStyle =
            hasEnableArray && !isInteractable
                ? {
                      pointerEvents: 'none',
                      opacity: 0.4,
                      filter: 'grayscale(60%)',
                  }
                : !isInteractable
                  ? { pointerEvents: 'none' }
                  : {}

        const hiddenStyle = !isVisible
            ? { visibility: 'hidden', pointerEvents: 'none' }
            : {}

        // Admin-configurable size for interactive inputs. scale=1 → current size.
        // Applied as a CSS transform so the aspect ratio is always preserved.
        const scaleVal = Number(comp.properties?.scale)
        const inputScale =
            isInteractiveType && Number.isFinite(scaleVal) && scaleVal > 0
                ? scaleVal
                : 1
        const baseTransform =
            comp.position.anchor === 'center' ? 'translate(-50%, -50%)' : 'none'
        const scaledTransform =
            inputScale !== 1
                ? `${baseTransform === 'none' ? '' : baseTransform} scale(${inputScale})`.trim()
                : baseTransform

        const style = {
            left: `${comp.position.x * 100}%`,
            top: `${comp.position.y * 100}%`,
            transform: scaledTransform,
            // Exposed so the text label can counter-scale itself and stay the
            // same size while the control image scales.
            '--input-scale': inputScale,
            ...hiddenStyle,
            ...interactiveStyle,
        }

        const device = sessionStorage.getItem('allowedDevice') || 'Toki'

        switch (comp.type) {
            case 'board': // canonical type (new saves)
            case 'toki_board': // legacy backward-compat
                // Determine the specific height rule for the board type
                const boardSpecificStyle =
                    device === 'Poki'
                        ? { height: '40%', width: 'auto' } // Scales down the Poki board relative to the viewport
                        : { height: '83%', width: 'auto' } // Preserves default Toki height

                // The Toki board artwork is NOT horizontally centered inside its
                // image file: the physical board's centre sits at 2180px in a
                // 3840px-wide image, i.e. (2180 - 1920) / 3840 ≈ 6.77% to the
                // right of the image centre. Nudge the image left by that
                // fraction of its own width so the actual board (not the image)
                // lands on comp.position.x. translate() percentages are relative
                // to the element's own width, so this holds at any rendered scale.
                const boardArtOffsetX =
                    device === 'Toki' ? -((2180 - 3840 / 2) / 3840) * 100 : 0 // % of image width
                const boardTranslateX =
                    (comp.position.anchor === 'center' ? -50 : 0) +
                    boardArtOffsetX
                const boardTranslateY =
                    comp.position.anchor === 'center' ? -50 : 0

                return (
                    <img
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component board-img${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            ...boardSpecificStyle,
                            transform: `translate(${boardTranslateX}%, ${boardTranslateY}%)`,
                        }}
                        src={resolveImage(comp.properties.image)}
                        alt={comp.name}
                        draggable={false}
                        onLoad={calculateWires}
                    />
                )
            case 'model': {
                // 1. Check if there's an active interaction override for this model
                const override = modelOverrides[comp.id] || {}
                const activeModelUrl = override.modelUrl || comp.modelUrl
                const activeAnimation =
                    override.animationName ||
                    currentStepData?.models?.animationName
                // 1. Check for camera orbit (theta, phi, radius) instead of x,y,z orientation
                const activeOrbit = override.cameraOrbit || comp.cameraOrbit

                // 2. Format: "theta(deg) phi(deg) radius(%)"
                const cameraOrbitAttr = activeOrbit
                    ? `${activeOrbit.theta || 0}deg ${activeOrbit.phi || 75}deg ${activeOrbit.radius || 100}%`
                    : undefined

                // 2. Resolve the final URL
                const resolvedUrl = resolveModelUrl(activeModelUrl)

                return (
                    <div
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component playscreen-model-viewer${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            width: comp.width || '350px',
                            height: comp.height || '450px',
                            zIndex: 5,
                        }}
                    >
                        <model-viewer
                            // Using resolvedUrl as the key forces React to completely remount the viewer
                            // if a completely different .glb file is loaded, avoiding crash errors.
                            key={resolvedUrl}
                            ref={(el) => (modelRefs.current[comp.id] = el)}
                            src={resolvedUrl}
                            alt={comp.name}
                            camera-controls="true"
                            shadow-intensity="1"
                            loading="eager"
                            // Pass the animation name dynamically
                            animation-name={activeAnimation || undefined}
                            // Force autoplay if there's an animation active
                            autoplay={
                                comp.autoplay || activeAnimation
                                    ? 'true'
                                    : undefined
                            }
                            camera-orbit={cameraOrbitAttr}
                            style={{
                                width: '100%',
                                height: '100%',
                                outline: 'none',
                                backgroundColor: 'transparent',
                            }}
                        />
                    </div>
                )
            }
            case 'image': {
                // Simple display-only image. scale=1 → 20vw base width; the image
                // keeps its natural aspect ratio (height auto).
                const scale = Number(comp.properties.scale)
                const safeScale = Number.isFinite(scale) ? scale : 1
                return (
                    <img
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component playscreen-image${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            width: `${safeScale * 20}vw`,
                            height: 'auto',
                            zIndex: 8,
                        }}
                        src={resolveImage(comp.properties.imageUrl)}
                        alt={comp.name || 'image'}
                        draggable={false}
                    />
                )
            }
            case 'output': {
                // Prefer input-driven outputImages; fall back to legacy resolveStateImage
                const imgSrcToRender =
                    outputImages[comp.id] ||
                    resolveStateImage(
                        comp.properties.states,
                        comp.properties.defaultImage
                    ) ||
                    comp.properties.offImage

                return (
                    <img
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component output-img ${
                            imgSrcToRender === 'dynamexMotor@2x.png'
                                ? 'output-dynamex'
                                : ''
                        }${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            zIndex: 10,
                            width: '800px !important',
                            height: 'auto !important',
                        }}
                        src={resolveImage(imgSrcToRender)}
                        alt={comp.name}
                        draggable={false}
                        onLoad={calculateWires}
                    />
                )
            }
            case 'custom_output': {
                // Prefer input-driven outputImages; fall back to legacy resolveStateImage
                const imgSrcToRender =
                    outputImages[comp.id] ||
                    resolveStateImage(
                        comp.properties.states,
                        comp.properties.defaultImage
                    ) ||
                    comp.properties.offImage

                return (
                    <img
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component output-img${highlightClass(comp.id)}`}
                        style={{ ...style, zIndex: 10 }}
                        src={resolveImage(imgSrcToRender)}
                        alt={comp.name}
                        draggable={false}
                        onLoad={calculateWires}
                    />
                )
            }
            case 'button_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveButtonControl
                        key={`${comp.id}-${currentStep}`}
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep} // <--- ADD THIS PROP
                    />
                )
            }
            case 'tack_button_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveTackButtonControl
                        key={`${comp.id}-${currentStep}`} // <-- Force sync on step jump
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep}
                        onRequestActivate={() =>
                            requestMomentaryPress('tack', comp.id)
                        }
                    />
                )
            }
            case 'hold_button_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveHoldButtonControl
                        key={`${comp.id}-${currentStep}`} // <-- Force sync on step jump
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep}
                        onRequestActivate={() => requestMomentaryPress('hold')}
                    />
                )
            }
            case 'trigger_button_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveTriggerButtonControl
                        key={`${comp.id}-${currentStep}`} // <-- Force sync on step jump
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep}
                    />
                )
            }
            case 'slider_control': {
                // Component type ranges from PortValuesRangeMapping / SliderRow
                const COMPONENT_RANGES = {
                    mini_geared_motor: { min: -100, max: 100, showLive: true },
                    geared_motor: { min: -100, max: 100, showLive: true },
                    dc_motor: { min: -100, max: 100, showLive: true },
                    dynamex_motor: { min: -100, max: 100, showLive: true },
                    servo_motor: { min: 0, max: 180, showLive: true },
                    servo_motor_270: { min: 0, max: 270, showLive: true },
                    servo_motor_sg90: { min: 0, max: 270, showLive: true },
                    servo_motor_360: { min: -100, max: 100, showLive: true },
                    pwm_3_pin_output: { min: 0, max: 100, showLive: true },
                    led: { min: 0, max: 100, showLive: true },
                    led_1c: { min: 0, max: 100, showLive: true },
                    digital_3_pin_output: { min: 0, max: 255, showLive: true },
                    analog_3_pin_input: { min: 0, max: 4095, showLive: false },
                    digital_3_pin_input: { min: 0, max: 1, showLive: false },
                    ultrasonic_sensor: { min: 0, max: 400, showLive: false },
                }

                const compType = comp.name.toLowerCase()
                const range = COMPONENT_RANGES[compType] || {
                    min: 0,
                    max: 100,
                    showLive: false,
                }

                return (
                    <div
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component slider-wrapper${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            zIndex: 20,
                        }}
                    >
                        <MotorSlider
                            mapping={comp.id}
                            outputStates={outputStates}
                            onChange={handleControlChange}
                            rangImgName={comp.properties.rangImgName}
                            title={comp.properties.title || 'Intensity'}
                            label={comp.properties.label || comp.name}
                            min={range.min}
                            max={range.max}
                            showLiveBtn={range.showLive}
                            port={comp.port}
                            componentType={compType}
                            isLiveStep={currentStepData?.isLiveStep}
                        />
                    </div>
                )
            }
            case 'custom_slider_control': {
                const bridgeComp = { ...comp, mapping: comp.id }
                const dynamicProps = generateSliderProps(
                    bridgeComp.mapping,
                    comp.properties.legacyName || bridgeComp.mapping
                )
                return (
                    <div
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component slider-wrapper${highlightClass(comp.id)}`}
                        style={{
                            ...style,
                            zIndex: 20,
                            width: '250px',
                            height: '60px',
                        }}
                    >
                        <SliderRange
                            rangImgName={comp.properties.rangImgName}
                            title={comp.properties.title}
                            componentName={
                                comp.properties.legacyName || bridgeComp.mapping
                            }
                            onChangehandler={(val) =>
                                handleControlChange(bridgeComp.mapping, val)
                            }
                            leftEyeData={() => {}}
                            disable={false}
                            webSerial={{ isConnected: true }}
                            {...dynamicProps}
                        />
                        <span className="control-label">{comp.name}</span>
                    </div>
                )
            }
            case 'rgb_control': {
                const rgbSliders = [
                    {
                        name: 'Red',
                        mapping: `${comp.id}_red`,
                        legacyName: 'L_red',
                        activeImg: 'red_slider',
                        inactiveImg: 'inactiveslider',
                    },
                    {
                        name: 'Green',
                        mapping: `${comp.id}_green`,
                        legacyName: 'L_green',
                        activeImg: 'green_slider',
                        inactiveImg: 'inactiveslider',
                    },
                    {
                        name: 'Blue',
                        mapping: `${comp.id}_blue`,
                        legacyName: 'L_blue',
                        activeImg: 'blue_slider',
                        inactiveImg: 'inactiveslider',
                    },
                ]

                return (
                    <div
                        key={comp.id}
                        id={comp.id}
                        className={`absolute-component rgb-control-wrapper${highlightClass(comp.id)}`}
                        style={{ ...style, zIndex: 20 }}
                    >
                        <RGBSlider
                            sliders={rgbSliders}
                            outputStates={outputStates}
                            onChange={handleControlChange}
                            generateSliderProps={generateSliderProps}
                            bgImage={resolveImage("renderImage('Eye_bg_Svg')")}
                            label={comp.properties.label}
                            port={comp.port}
                            isLiveStep={currentStepData?.isLiveStep}
                        />
                    </div>
                )
            }
            case 'rgb_button_control': {
                // Merged into LiveButtonControl – RGB mode is detected via comp.name === 'RGB'
                const bridgeComp = { ...comp, mapping: comp.id }
                return (
                    <LiveButtonControl
                        key={`${comp.id}-${currentStep}`}
                        comp={bridgeComp}
                        outputStates={outputStates}
                        onChange={(_, val) => handleControlChange(comp.id, val)}
                        resolveImage={resolveImage}
                        highlightClass={highlightClass(comp.id)}
                        style={style}
                        isLiveStep={currentStepData?.isLiveStep}
                    />
                )
            }
            default:
                return null
        }
    }

    if (loading)
        return (
            <div className="play-app-container centered-msg">
                Loading Play Screen...
            </div>
        )
    if (!data || !data.components)
        return (
            <div className="play-app-container centered-msg">
                No Play Screen Data
            </div>
        )

    const isFinalStep =
        hasSteps && currentStep > totalSteps && !hideFinalStep
    const isFirstStep = !hasSteps || currentStep <= 1
    // In Kit Menu play the last real step is terminal (no Congratulations step),
    // so the Next button is disabled once the user reaches it.
    const isTerminalStep = hideFinalStep && currentStep >= totalSteps
    // Kit Menu play with a single step has nothing to navigate, so hide the
    // footer entirely.
    const hideFooter = hideFinalStep && totalSteps === 1

    return (
        <div className="play-app-container" ref={appContainerRef}>
            {/* --- NEW: The Portrait/Mobile Restriction Overlay --- */}
            {isMobilePortrait && (
                <div className="portrait-overlay">
                    <div className="completion-content">
                        <div className="portrait-icon">
                            <svg viewBox="0 0 24 24" width="80" height="80">
                                <rect
                                    x="2"
                                    y="4"
                                    width="20"
                                    height="12"
                                    rx="2"
                                    fill="none"
                                    stroke="#f8b26a"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M6 20h12M12 16v4"
                                    stroke="#f8b26a"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <h1 className="portrait-title">Switch Device</h1>
                        <p className="portrait-subtitle">
                            Please access this content on a{' '}
                            <strong>PC, Laptop, or a Landscape Tablet</strong>.
                            Portrait ratios and mobile devices are not
                            supported.
                        </p>
                    </div>
                </div>
            )}

            {/* --- NEW: Floating Exit Preview Button --- */}
            {isPreviewMode && (
                <button
                    onClick={() => history.goBack()}
                    style={{
                        position: 'fixed',
                        top: '12vh', // Just below the header
                        right: '30px',
                        zIndex: 999999,
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '50px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <MdOutlineArrowBack size={18} /> Exit Preview
                </button>
            )}
            {/* HEADER — hidden in shell mode (SubprogramRunner renders it persistently) */}
            {!isShellMode && (
                <div className="play-header">
                    <HeaderComponent
                        showTimeline={isMake || isPreviewMode}
                        title={data.title || 'Play Mode'}
                        titlePosition="left"
                        help_btnPosition="right"
                        isGoBack={true}
                        goBack={goBack}
                        showHelp={true}
                        showBluetoothBtn={true}
                        currentStep={
                            hasSteps
                                ? Math.min(currentStep, totalSteps)
                                : undefined
                        }
                        totalSteps={hasSteps ? totalSteps : undefined}
                        stepTitles={
                            hasSteps
                                ? data?.steps?.map(
                                      (step) => step?.title || ''
                                  ) || []
                                : []
                        }
                        onStepClick={(stepNum) => {
                            setUserInteractedThisStep(false)
                            // Auto-fill state for whatever step they clicked
                            setOutputStates(
                                buildStateUpToStep(
                                    stepNum - 1,
                                    data?.steps || []
                                ) // set to just the step before
                            )
                            setCurrentStep(stepNum)
                            if (aiVoiceover) playAudioForStep(stepNum)
                            setShowSubtitleDisplay(true)
                        }}
                    />
                </div>
            )}
            <main className="play-content">
                <div className="safe-area-container" ref={safeAreaRef}>
                    <svg className="wiring-layer">
                        {wires.map((wire) => (
                            <path
                                key={wire.id}
                                d={wire.path}
                                className="wire-path"
                            />
                        ))}
                    </svg>
                    {data.components.map(renderComponent)}
                </div>

                {/* AI Speaking Pill / Subtitle Box — morphs between shapes */}
                {currentInstruction && (
                    <div
                        ref={subtitleRef}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            left: '50%',
                            transform: !subtitleVisible
                                ? 'translateX(-50%) translateY(-120vh)'
                                : 'translateX(-50%)',
                            maxWidth: !showSubtitleDisplay ? '280px' : '600px',
                            maxHeight: !showSubtitleDisplay ? '44px' : '150px',
                            borderRadius: !showSubtitleDisplay
                                ? '20px'
                                : '20px',
                            padding: !showSubtitleDisplay
                                ? '0 18px'
                                : '12px 24px',
                            background: !showSubtitleDisplay
                                ? 'rgba(60, 60, 120, 0.13)'
                                : 'rgba(255, 255, 255, 0.3)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            display: 'grid',
                            gridTemplateRows: !showSubtitleDisplay
                                ? '44px'
                                : 'auto',
                            alignItems: 'center',
                            justifyItems: 'center',
                            overflow: 'hidden',
                            zIndex: 10001,
                            cursor: !showSubtitleDisplay
                                ? 'pointer'
                                : 'default',
                            opacity: !subtitleVisible ? 0 : 1,
                            pointerEvents: !subtitleVisible ? 'none' : 'auto',
                            transition: [
                                'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'border-radius 0.45s ease',
                                'padding 0.4s ease',
                                'background 0.45s ease',
                                'opacity 0.6s ease',
                            ].join(', '),
                        }}
                        onClick={
                            !showSubtitleDisplay
                                ? () => setShowSubtitleDisplay(true)
                                : undefined
                        }
                    >
                        {/* Pill content — visible when collapsed */}
                        <div
                            style={{
                                gridArea: '1/1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: '#2f2e65',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                                whiteSpace: 'nowrap',
                                opacity: !showSubtitleDisplay ? 1 : 0,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0.25s'
                                    : '0s',
                                pointerEvents: 'none',
                            }}
                        >
                            <BsStars size={20} />{' '}
                            {aiVoiceover
                                ? 'AI is speaking...'
                                : 'Click to expand'}
                        </div>
                        {/* Subtitle text — visible when expanded */}
                        <div
                            style={{
                                gridArea: '1/1',
                                fontSize: '1.1rem',
                                color: '#333',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'Halcyon Regular, sans-serif',
                                lineHeight: '1.5',
                                textAlign: 'center',
                                opacity: !showSubtitleDisplay ? 0 : 1,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0s'
                                    : '0.25s',
                                pointerEvents: 'none',
                                width: '100%',
                            }}
                        >
                            {aiVoiceover ? typewriterText : currentInstruction}
                        </div>
                    </div>
                )}

                {/* Bottom notice (please wait / please connect) */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: '30px',
                        left: '50%',
                        transform: noticeText
                            ? 'translateX(-50%) translateY(0)'
                            : 'translateX(-50%) translateY(20px)',
                        padding: '12px 24px',
                        borderRadius: '20px',
                        background: 'rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        color: '#2f2e65',
                        fontWeight: 600,
                        fontSize: '1rem',
                        fontFamily: 'Halcyon_SemiBold, sans-serif',
                        whiteSpace: 'nowrap',
                        zIndex: 10001,
                        opacity: noticeText ? 1 : 0,
                        pointerEvents: 'none',
                        transition: 'opacity 0.3s ease, transform 0.3s ease',
                    }}
                >
                    {noticeText || ' '}
                </div>

                {!isShellMode && (
                    <div className="toolbar-sidebar">
                        <button
                            className={`toolbar-button ${aiVoiceover ? 'active' : ''}`}
                            title="AI Voiceover"
                            onClick={() => {
                                handleAiVoiceoverToggle()
                                setSubtitleVisible(!aiVoiceover)
                                setShowSubtitleDisplay(!aiVoiceover)
                            }}
                        >
                            <BsStars />
                        </button>
                        <button
                            className="toolbar-button"
                            title="Fullscreen"
                            onClick={handleFullscreen}
                        >
                            <MdFullscreen />
                        </button>
                        <button
                            className="toolbar-button"
                            title="Reset"
                            disabled={!hasSteps}
                            onClick={resetTutorial}
                        >
                            <RxReset />
                        </button>
                    </div>
                )}
                <audio ref={audioRef} />
            </main>
            {/* FOOTER — hidden in shell mode (SubprogramRunner renders it
                persistently), and in single-step Kit Menu play (nothing to
                navigate). */}
            {!isShellMode && !hideFooter && (
                <footer className="play-footer">
                    <div className="step-counter">
                        {hasSteps
                            ? isFinalStep
                                ? 'Complete'
                                : `Step ${currentStep} of ${totalSteps}`
                            : ''}
                    </div>
                    <div className="step-title">
                        {hasSteps
                            ? currentStepData?.title || ''
                            : data.title || 'Play Mode'}
                    </div>
                    <div className="nav-buttons">
                        <button
                            className="nav-btn"
                            onClick={handleStepPrev}
                            disabled={isFirstStep && !dataProp}
                        >
                            <GrPrevious />
                            <span className="footer-btn-text">Previous</span>
                        </button>

                        {/* The Next Button dynamically disables if validations are not met */}
                        <div className="next-btn-wrapper">
                            {!isStepValid && (
                                <div className="next-btn-tooltip">
                                    <IoInformationCircle /> Complete the action
                                    to proceed
                                </div>
                            )}
                            <button
                                className={`nav-btn ${currentStepData?.isFreeStep ? 'free-step-highlight' : ''}`}
                                onClick={handleStepNext}
                                disabled={
                                    (isFinalStep && !dataProp) ||
                                    !isStepValid ||
                                    isTerminalStep
                                }
                            >
                                <span className="footer-btn-text">
                                    {isFinalStep ? 'Continue' : 'Next'}
                                </span>
                                <GrNext />
                            </button>
                        </div>
                    </div>
                </footer>
            )}
        </div>
    )
}

export default PlayScreen
